import { SocialPageClient } from "@/components/social/SocialPageClient";
import { useMemo } from "react";
import { SocialPost, SocialStory, JartinetHermandad, Banda, Organizacion } from "@/lib/types";
import { collection, query, where, orderBy, limit } from "firebase/firestore";
import { db } from "@/firebase/config";
import { sanitizeFirestoreData } from "@/lib/utils";
import { useCollection } from "@/firebase/firestore/use-collection";

/**
 * Escaparate público de la red social. Visible sin login (las colecciones son
 * de lectura pública). Cualquier interacción muestra el aviso de "debes ser
 * ciudadano de Jartiland" (publicMode en SocialPageClient).
 */
export default function PublicSocialPage() {
    // Solo los 150 posts más recientes: el feed no scrollea más allá y cada
    // visita leía la colección ENTERA (era el grueso del coste de Firestore, [158]).
    const postsQuery = useMemo(
        () => query(collection(db, 'social_posts'), orderBy('createdAt', 'desc'), limit(150)),
        []
    );
    const { data: rawPosts } = useCollection<SocialPost>(postsQuery);

    const storiesQuery = useMemo(() => {
        const now = new Date();
        return query(collection(db, 'social_stories'), where('expiresAt', '>', now));
    }, []);
    const { data: rawStories } = useCollection<SocialStory>(storiesQuery);

    const hermandadesQuery = useMemo(() => query(collection(db, 'jartinet_hermandades')), []);
    const { data: rawHermandades } = useCollection<JartinetHermandad>(hermandadesQuery);
    const bandasQuery = useMemo(() => query(collection(db, 'bandas')), []);
    const { data: rawBandas } = useCollection<Banda>(bandasQuery);
    const orgsQuery = useMemo(() => query(collection(db, 'organizaciones')), []);
    const { data: rawOrgs } = useCollection<Organizacion>(orgsQuery);

    const allPosts = useMemo(
        () => (rawPosts || []).map(p => sanitizeFirestoreData(p) as SocialPost),
        [rawPosts]
    );

    const hermandades = useMemo(() => (rawHermandades || []).map(h => sanitizeFirestoreData(h) as JartinetHermandad), [rawHermandades]);
    const bandas = useMemo(() => (rawBandas || []).map(b => sanitizeFirestoreData(b) as Banda), [rawBandas]);
    const organizaciones = useMemo(() => (rawOrgs || []).map(o => sanitizeFirestoreData(o) as Organizacion), [rawOrgs]);

    const storiesByAuthor = useMemo(() => {
        return (rawStories || []).reduce((acc, story) => {
            const sanitized = sanitizeFirestoreData(story) as SocialStory;
            const key = `${sanitized.authorType}:${sanitized.authorId}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(sanitized);
            return acc;
        }, {} as Record<string, SocialStory[]>);
    }, [rawStories]);

    const pinnedPostIds = useMemo(() => {
        const set = new Set<string>();
        [...hermandades, ...bandas, ...organizaciones].forEach(profile => {
            if (profile.pinnedPosts) {
                Object.values(profile.pinnedPosts).forEach(id => { if (id) set.add(id); });
            }
        });
        return set;
    }, [hermandades, bandas, organizaciones]);

    const mainFeedPosts = useMemo(
        () => allPosts.filter(p => !pinnedPostIds.has(p.id)),
        [allPosts, pinnedPostIds]
    );

    return (
        <div className="container mx-auto px-4 py-8 min-h-screen">
            <SocialPageClient
                initialHermandades={hermandades}
                initialBandas={bandas}
                initialOrganizaciones={organizaciones}
                initialPosts={mainFeedPosts}
                initialStories={storiesByAuthor}
                initialUserProfiles={{}}
                allPosts={allPosts}
                currentUser={null}
                publicMode
            />
        </div>
    );
}
